/**
 * BrandCampaignReportPanel — Brand-side end-of-campaign report viewer.
 *
 * Renders inside CampaignProgressTab. Shows aggregated KPI tiles, per-creator
 * metric table, and PDF / Excel download buttons (proxied through axios so we
 * get streaming binary blobs without leaving the SPA).
 *
 * Strictly additive — read-only here. Brand cannot edit metrics.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Loader2, FileText, FileSpreadsheet, ExternalLink, BarChart3,
  TrendingUp, Eye, Heart, MessageCircle, Share2, Bookmark, Link2,
  Target, Zap, IndianRupee, Gauge,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

export const BrandCampaignReportPanel = ({ campaignId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'excel' | null
  const [sharing, setSharing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/brand/campaigns/${campaignId}/report`,
        { withCredentials: true },
      );
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaignId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const download = async (kind /* 'pdf' | 'excel' */) => {
    setDownloading(kind);
    try {
      const res = await axios.get(
        `${API_URL}/api/brand/campaigns/${campaignId}/report/${kind}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const mime = kind === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
      a.download = `${(data?.campaign_name || 'campaign').replace(/\s+/g, '_')}_report.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  // ---- Share link (mirror of campaign-plans share pattern) -----------------
  const copyShareLink = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      // Use any token already on the loaded payload; else POST to generate.
      let token = data?.report_share_token;
      if (!token) {
        const res = await axios.post(
          `${API_URL}/api/brand/campaigns/${campaignId}/generate-share-token`,
          {},
          { withCredentials: true },
        );
        token = res.data.report_share_token;
        setData((d) => (d ? { ...d, report_share_token: token } : d));
      }
      const link = `${window.location.origin}/reports/share/${token}`;
      await navigator.clipboard.writeText(link);
      toast.success('Share link copied — anyone with it can view this report (read-only)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create share link');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center text-[#6B6B6B] dark:text-zinc-500" data-testid="report-loading">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading report…
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary || {};
  const rows = data.creators || [];
  const totals = s.totals || {};
  const hasData = (s.reported_creators || 0) > 0;
  // Feature 2 — ROI Calculator payload from backend
  const roi = data.roi_summary || null;

  return (
    <div className="space-y-4 pt-4 border-t border-black/[0.08] dark:border-white/5" data-testid="brand-campaign-report-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-[#FF451A]" />
            <h3 className="text-lg font-bold text-[#0F0F0F] dark:text-white">Campaign Completion Report</h3>
          </div>
          <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono mt-1">
            {s.reported_creators ?? 0} / {s.total_creators ?? 0} creators reported · {s.completion_pct ?? 0}% complete
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={copyShareLink}
            disabled={sharing}
            className="bg-white/5 hover:bg-white/10 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10 rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
            data-testid="report-share-btn"
            title="Copy a public read-only link"
          >
            {sharing ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
            Share Report
          </Button>
          <Button
            onClick={() => download('pdf')}
            disabled={!hasData || downloading === 'pdf'}
            className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="report-download-pdf"
          >
            {downloading === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Download PDF
          </Button>
          <Button
            onClick={() => download('excel')}
            disabled={!hasData || downloading === 'excel'}
            className="bg-[#00C972] hover:bg-[#00C972]/85 text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="report-download-excel"
          >
            {downloading === 'excel' ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Download Excel
          </Button>
        </div>
      </div>

      {/* Aggregate KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="report-aggregates">
        <Tile icon={Eye}           label="Total Views"    value={fmtNum(totals.views)}      color="#06B6D4" testid="agg-views" />
        <Tile icon={TrendingUp}    label="Total Reach"    value={fmtNum(totals.reach)}      color="#FFB800" testid="agg-reach" />
        <Tile icon={Heart}         label="Total Likes"    value={fmtNum(totals.likes)}      color="#FF3B30" testid="agg-likes" />
        <Tile icon={MessageCircle} label="Total Comments" value={fmtNum(totals.comments)}   color="#A855F7" testid="agg-comments" />
        <Tile icon={Share2}        label="Total Shares"   value={fmtNum(totals.shares)}     color="#0055FF" testid="agg-shares" />
        <Tile icon={Bookmark}      label="Total Saves"    value={fmtNum(totals.saves)}      color="#22C55E" testid="agg-saves" />
      </div>

      {/* Secondary band */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Tile label="Followers Reached" value={fmtNum(s.total_followers_reached)} color="#FF451A" testid="agg-followers" />
        <Tile
          label="Avg Engagement Rate"
          value={s.avg_engagement_rate != null ? `${s.avg_engagement_rate}%` : '—'}
          color="#00C972"
          testid="agg-er"
        />
        <Tile
          label="Reporting Coverage"
          value={`${s.reported_creators ?? 0} / ${s.total_creators ?? 0}`}
          sub={`${s.completion_pct ?? 0}%`}
          color="#9B6EFF"
          testid="agg-coverage"
        />
      </div>

      {/* Feature 2 — ROI Summary block (above the per-creator table) */}
      <RoiSummary roi={roi} />

      {/* Per-creator table */}
      {!hasData ? (
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-6 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="report-empty">
          No metrics reported yet. Your Campaign Manager will fill these in once creators have posted.
        </div>
      ) : (
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl overflow-x-auto" data-testid="report-table-wrap">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-white/[0.03] border-b border-black/[0.08] dark:border-white/5">
              <tr className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
                <th className="text-left py-2.5 px-3">Creator</th>
                <th className="text-right py-2.5 px-2">Views</th>
                <th className="text-right py-2.5 px-2">Reach</th>
                <th className="text-right py-2.5 px-2">Likes</th>
                <th className="text-right py-2.5 px-2">Comments</th>
                <th className="text-right py-2.5 px-2">Shares</th>
                <th className="text-right py-2.5 px-2">Saves</th>
                <th className="text-right py-2.5 px-2">ER %</th>
                <th className="text-left py-2.5 px-2">Post</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-testid="report-rows">
              {rows.map((r) => {
                const m = r.completion_metrics || {};
                const reported = !!m.live_post_link;
                return (
                  <tr key={r.row_id} className={reported ? '' : 'opacity-50'} data-testid={`report-row-${r.row_id}`}>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        {r.profile_photo_url ? (
                          <img src={r.profile_photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold">
                            {r.creator_name?.[0]?.toUpperCase() || '·'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-[#0F0F0F] dark:text-white text-xs font-medium truncate max-w-[160px]">{r.creator_name}</div>
                          {r.instagram_handle && (
                            <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500">@{r.instagram_handle}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.views)}</td>
                    <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.reach)}</td>
                    <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.likes)}</td>
                    <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.comments)}</td>
                    <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.shares)}</td>
                    <td className="py-2 px-2 text-right text-zinc-200 font-mono text-xs">{fmtNum(m.saves)}</td>
                    <td className="py-2 px-2 text-right font-mono text-xs">
                      {m.engagement_rate != null ? (
                        <span className="text-[#FF451A]">{m.engagement_rate}%</span>
                      ) : (
                        <span className="text-[#A1A1AA] dark:text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      {m.live_post_link ? (
                        <a
                          href={m.live_post_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#06B6D4] hover:underline inline-flex items-center gap-1 text-xs font-mono"
                        >
                          <ExternalLink size={10} /> Open
                        </a>
                      ) : (
                        <span className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 font-mono">pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// --- Sub-components ----------------------------------------------------------
const Tile = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">
      {Icon && <Icon size={10} style={{ color }} />} {label}
    </div>
    <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">{sub}</div>}
  </div>
);

// -----------------------------------------------------------------------------
// Feature 2 — Campaign ROI Calculator block
//
// Renders 4 cards (CPV vs benchmark · ER vs benchmark · Total Reach · Efficiency
// Score 1-10) and a one-line vs-industry-average verdict.  Handles empty state
// with the exact copy the user asked for.
// -----------------------------------------------------------------------------
const fmtInrCompact = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  if (v >= 10_000_000) return `\u20B9${(v / 10_000_000).toFixed(1)}Cr`;
  if (v >= 100_000) return `\u20B9${(v / 100_000).toFixed(1)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${v.toLocaleString('en-IN')}`;
};

const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));

const RoiSummary = ({ roi }) => {
  if (!roi) return null;

  // Empty state — campaign not completed OR nobody has posted metrics.
  if (roi.empty) {
    return (
      <div
        className="bg-white dark:bg-[#0A0B0D] border border-dashed border-black/[0.10] dark:border-white/10 rounded-xl p-5 text-sm text-[#6B6B6B] dark:text-zinc-500 flex items-center gap-3"
        data-testid="roi-summary-empty"
      >
        <Gauge size={18} className="text-[#FF451A]" />
        <div>Complete campaign metrics to see ROI analysis.</div>
      </div>
    );
  }

  // Efficiency Score (1-10): average of CPV score and ER score.
  // CPV: cheaper than benchmark = higher score.  ER: higher than benchmark = higher score.
  // When CPV is unknown (0 spend / no invoices), score off ER alone.
  const scoreEr  = clamp(5 + roi.vs_benchmark_er / 20, 0, 10);
  const scoreCpv = clamp(5 - roi.vs_benchmark_cpv / 20, 0, 10);
  const cpvKnownForScore = roi.cost_per_view > 0;
  const efficiency = Math.round(clamp(cpvKnownForScore ? (scoreEr + scoreCpv) / 2 : scoreEr, 1, 10));

  // Verdict — combine deltas so cheaper-CPV and higher-ER both count as
  // positive.  Flip CPV delta sign so "-30% CPV" reads as +30% better.
  // If CPV is unknown, use only the ER delta.
  const combined = cpvKnownForScore
    ? ((-roi.vs_benchmark_cpv) + roi.vs_benchmark_er) / 2
    : roi.vs_benchmark_er;
  const verdictBetter = combined >= 0;
  const verdictPct = Math.abs(Math.round(combined));

  // Card colouring — green for better than benchmark, red for worse.
  // When cost_per_view is 0 (no invoice data yet), don't paint it red — treat
  // as unknown/neutral so brands don't misread a data-gap as bad performance.
  const CPV_KNOWN = roi.cost_per_view > 0;
  const CPV_BETTER = CPV_KNOWN && roi.cost_per_view < roi.industry_benchmark_cpv;
  const ER_BETTER  = roi.engagement_rate >= roi.industry_benchmark_er;

  const cardBase = 'rounded-xl p-4 border';
  const goodCls = 'bg-[#00C972]/5 border-[#00C972]/25';
  const badCls  = 'bg-[#FF3B30]/5 border-[#FF3B30]/25';
  const neutralCls = 'bg-white dark:bg-[#0A0B0D] border-black/[0.08] dark:border-white/10';
  const cpvCardCls = !CPV_KNOWN ? neutralCls : (CPV_BETTER ? goodCls : badCls);
  const cpvValueCls = !CPV_KNOWN ? 'text-[#6B6B6B] dark:text-zinc-400' : (CPV_BETTER ? 'text-[#00C972]' : 'text-[#FF3B30]');

  return (
    <div className="space-y-2" data-testid="roi-summary">
      <div className="flex items-center gap-2 pt-2">
        <Gauge size={14} className="text-[#FF451A]" />
        <h4 className="text-sm font-bold uppercase tracking-wider text-[#0F0F0F] dark:text-white">ROI Analysis</h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {/* Card 1 — CPV vs benchmark */}
        <div className={`${cardBase} ${cpvCardCls}`} data-testid="roi-card-cpv">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 font-mono text-[#6B6B6B] dark:text-zinc-400">
            <IndianRupee size={10} /> Cost per View
          </div>
          <div className={`text-2xl font-bold font-mono ${cpvValueCls}`}>
            {CPV_KNOWN ? `\u20B9${roi.cost_per_view.toFixed(2)}` : '—'}
          </div>
          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">
            {`Industry benchmark \u20B9${roi.industry_benchmark_cpv.toFixed(2)}`}
          </div>
        </div>

        {/* Card 2 — ER vs benchmark */}
        <div className={`${cardBase} ${ER_BETTER ? goodCls : badCls}`} data-testid="roi-card-er">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 font-mono text-[#6B6B6B] dark:text-zinc-400">
            <TrendingUp size={10} /> Engagement Rate
          </div>
          <div className={`text-2xl font-bold font-mono ${ER_BETTER ? 'text-[#00C972]' : 'text-[#FF3B30]'}`}>
            {`${roi.engagement_rate.toFixed(2)}%`}
          </div>
          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">
            {`Industry benchmark ${roi.industry_benchmark_er}%`}
          </div>
        </div>

        {/* Card 3 — Total Reach (neutral) */}
        <div className={`${cardBase} ${neutralCls}`} data-testid="roi-card-reach">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 font-mono text-[#6B6B6B] dark:text-zinc-400">
            <Target size={10} className="text-[#FF451A]" /> Total Reach
          </div>
          <div className="text-2xl font-bold font-mono text-[#0F0F0F] dark:text-white">
            {fmtNum(roi.total_reach)}
          </div>
          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">
            {`Spend ${fmtInrCompact(roi.total_spend)}`}
          </div>
        </div>

        {/* Card 4 — Efficiency Score 1-10 */}
        <div className={`${cardBase} ${neutralCls}`} data-testid="roi-card-efficiency">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 font-mono text-[#6B6B6B] dark:text-zinc-400">
            <Zap size={10} className="text-[#FFB800]" /> Efficiency Score
          </div>
          <div className="text-2xl font-bold font-mono text-[#0F0F0F] dark:text-white">
            {efficiency}<span className="text-sm text-[#6B6B6B] dark:text-zinc-500 font-normal">/10</span>
          </div>
          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">
            {efficiency >= 8 ? 'Excellent' : efficiency >= 6 ? 'Strong' : efficiency >= 4 ? 'Average' : 'Below average'}
          </div>
        </div>
      </div>

      {/* Verdict line */}
      <div
        className={`text-sm font-medium ${verdictBetter ? 'text-[#00C972]' : 'text-[#FF3B30]'}`}
        data-testid="roi-verdict"
      >
        This campaign performed <span className="font-bold">{verdictPct}%</span> {verdictBetter ? 'better' : 'worse'} than the industry average.
      </div>
    </div>
  );
};
